import { useMemo } from 'react'

import ReactSelect, {
  MenuPlacement,
  components,
  ControlProps,
} from 'react-select'

import { A11yText } from 'components/A11yText'
import { IconType } from 'components/icons'

import { classNamePrefix, SelectContainer } from './styles'

type Option = {
  value: string
  label: string
}
type SelectProps = {
  options: Array<Option>
  value: string
  label: string
  menuPlacement?: MenuPlacement
  labelAsValueDisplay?: boolean
  menuAsPortal?: boolean
  positionRight?: boolean
  icon?: IconType
  dark?: boolean
  onChange: (option: Option['value'] | null) => void
  disabled?: boolean
}

const getValue = (options: Array<Option>, value: string): Option | undefined =>
  options.find((option) => option.value === value) ?? undefined

const selectComponents = (Icon: IconType) => ({
  Control: ({ children, ...props }: ControlProps) => (
    <components.Control className="iconButton" {...props}>
      <Icon size="medium" />
      <span>{children}</span>
    </components.Control>
  ),
})
export const Select = ({
  options,
  value,
  onChange,
  icon,
  label,
  dark,
  menuAsPortal,
  menuPlacement,
  positionRight,
  labelAsValueDisplay,
  disabled,
}: SelectProps) => {
  const internalValue = useMemo(
    () => getValue(options, value),
    [options, value]
  )

  const componentProps = useMemo(
    () => (icon ? selectComponents(icon) : {}),
    [icon]
  )

  const createModalRoot = () => {
    const element = document.createElement('div')
    element.setAttribute('id', 'modal-root')
    document.body.appendChild(element)
    return element
  }
  const portalRoot = document.getElementById('modal-root') || createModalRoot()

  return (
    <SelectContainer dark={dark} positionRight={positionRight}>
      <A11yText id={label}>{label}</A11yText>
      <ReactSelect
        closeMenuOnScroll={(e) => {
          if (e.target === document) {
            return true
          }
          return false
        }}
        isDisabled={disabled}
        aria-labelledby={label}
        components={componentProps}
        menuPlacement={menuPlacement}
        classNamePrefix={classNamePrefix}
        onChange={(option) => onChange(option?.value ?? null)}
        options={options}
        value={
          labelAsValueDisplay
            ? { label, value: internalValue?.value }
            : internalValue
        }
        isSearchable={false}
        menuPortalTarget={menuAsPortal ? portalRoot : undefined}
      />
    </SelectContainer>
  )
}
